import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wrench, Plus, RefreshCw, TrendingUp, TrendingDown, Calculator, Save, Info, Package, Link2Off, Trash2, Pencil, Layers } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import api, { fmtIDR } from '@/lib/api';
import { toast } from 'sonner';

const DEFAULT_CONST = 0.006165;
const DEFAULT_TOL = 0.3;
const DEFAULT_LEN = 12.0;

function computeWeight({ formula, nominal_size_mm, tolerance_mm, length_m, formula_constant, weight_kg }) {
  const ceil2 = (x) => Math.ceil(x * 100) / 100;
  if (formula === 'manual') return ceil2(Number(weight_kg) || 0);
  const nom = Number(nominal_size_mm) || 0;
  const tol = Number(tolerance_mm) ?? DEFAULT_TOL;
  const len = Number(length_m) ?? DEFAULT_LEN;
  const c = Number(formula_constant) ?? DEFAULT_CONST;
  const real = Math.max(0, nom - tol);
  return ceil2(c * (real ** 2) * len);
}

export default function OwnerIronPrices() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priceDrafts, setPriceDrafts] = useState({});          // { iron_type: string }
  const [addTypeOpen, setAddTypeOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [savingType, setSavingType] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/iron/prices');
      setData(data);
      const drafts = {};
      (data.types || []).forEach((t) => { drafts[t.iron_type] = String(t.price_per_kg || ''); });
      setPriceDrafts(drafts);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal memuat data'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savePrice = async (ironType) => {
    const price = Number(priceDrafts[ironType]);
    if (isNaN(price) || price < 0) return toast.error('Harga tidak valid');
    setSavingType(ironType);
    try {
      const res = await api.put(`/iron/prices/${encodeURIComponent(ironType)}`, { price_per_kg: price });
      toast.success(res.data.message);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSavingType(''); }
  };

  const adjustPercent = async (ironType, percent) => {
    setSavingType(ironType);
    try {
      const res = await api.post(`/iron/prices/${encodeURIComponent(ironType)}/adjust`, { percent });
      toast.success(res.data.message);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal adjust'); }
    finally { setSavingType(''); }
  };

  const unlinkProduct = async (productId, productName) => {
    try {
      await api.patch(`/products/${productId}/iron`, { iron: null });
      toast.success(`"${productName}" dilepas dari kategori besi (produk tetap ada di database)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal unlink'); }
  };

  const deleteProduct = async (productId, productName) => {
    try {
      await api.delete(`/products/${productId}`);
      toast.success(`Produk "${productName}" dihapus dari database`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal hapus produk'); }
  };

  // Hapus jenis besi (default: unlink semua produk terkait, produknya tetap ada)
  const deleteIronType = async (ironType) => {
    try {
      const { data: res } = await api.delete(`/iron/prices/${encodeURIComponent(ironType)}`);
      toast.success(res.message || `Jenis "${ironType}" dihapus`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal hapus jenis besi'); }
  };

  // Rename jenis besi (update settings + semua produk terafiliasi)
  const [renameOpen, setRenameOpen] = useState(null);   // holds current iron_type being renamed
  const renameIronType = async (ironType, newName) => {
    try {
      const { data: res } = await api.patch(`/iron/prices/${encodeURIComponent(ironType)}/rename`, { new_name: newName });
      toast.success(res.message || `${ironType} → ${newName}`);
      setRenameOpen(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal rename jenis besi'); }
  };

  const [createOpenType, setCreateOpenType] = useState(null);

  const totalProducts = useMemo(() => (data?.types || []).reduce((a, t) => a + (t.product_count || 0), 0), [data]);

  return (
    <AppShell nav={ownerNav} title="Manajemen Harga Besi">
      <div className="space-y-5">
        {/* Info */}
        <Card>
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm space-y-1">
              <div><b>Rumus round bar (Besi Beton):</b> berat = 0.006165 × (nominal − 0.3)² × 12 m per batang</div>
              <div>Contoh: Besi 8 → real 7.7mm → berat = 0.006165 × 59.29 × 12 = <b>4.386 kg</b> per batang</div>
              <div className="text-muted-foreground">Ubah harga per kg di sini → <b>semua produk otomatis di-recalculate</b> (harga jual = berat × harga/kg).</div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Jenis Besi Terdaftar</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{data?.types?.length || 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Total Produk Besi</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{totalProducts}</div>
          </CardContent></Card>
          <Card className="flex items-center"><CardContent className="p-4 w-full flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="iron-refresh">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="iron-attach-btn"><Wrench className="h-4 w-4 mr-1.5" />Set Config Produk</Button>
              </DialogTrigger>
              <AttachIronConfigDialog onSaved={() => { setAttachOpen(false); load(); }} />
            </Dialog>
            <Dialog open={addTypeOpen} onOpenChange={setAddTypeOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="iron-add-type-btn"><Plus className="h-4 w-4 mr-1.5" />Tambah Jenis Besi</Button>
              </DialogTrigger>
              <AddTypeDialog onAdd={async (name, price) => {
                try {
                  await api.put(`/iron/prices/${encodeURIComponent(name)}`, { price_per_kg: price });
                  toast.success(`Jenis "${name}" ditambahkan`);
                  setAddTypeOpen(false);
                  load();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal'); }
              }} />
            </Dialog>
          </CardContent></Card>
        </div>

        {/* Empty state */}
        {(data?.types || []).length === 0 ? (
          <Card><CardContent className="p-8 text-center space-y-3">
            <Wrench className="h-10 w-10 mx-auto text-muted-foreground" />
            <div className="font-medium">Belum ada jenis besi terdaftar</div>
            <div className="text-sm text-muted-foreground">Klik <b>+ Tambah Jenis Besi</b> untuk mulai (mis. Besi Beton, Baja IWF, Besi Hollow).</div>
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {data.types.map((t) => (
              <Card key={t.iron_type} data-testid={`iron-type-card-${t.iron_type}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Wrench className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{t.iron_type}</CardTitle>
                      <Badge variant="secondary">{t.product_count} produk</Badge>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Label className="text-xs text-muted-foreground">Harga/kg (Rp)</Label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">Rp</span>
                        <Input
                          type="number" min="0" step="1"
                          className="w-36 h-9 pl-8 tabular-nums font-semibold"
                          value={priceDrafts[t.iron_type] || ''}
                          onChange={(e) => setPriceDrafts((d) => ({ ...d, [t.iron_type]: e.target.value }))}
                          data-testid={`iron-price-input-${t.iron_type}`}
                        />
                      </div>
                      <Button size="sm" onClick={() => savePrice(t.iron_type)} disabled={savingType === t.iron_type} data-testid={`iron-save-${t.iron_type}`}>
                        <Save className="h-3.5 w-3.5 mr-1" /> Simpan & Terapkan
                      </Button>
                      <div className="inline-flex items-center gap-1 border border-border/60 rounded-md">
                        <Button size="sm" variant="ghost" className="h-9 px-2 text-rose-600 hover:bg-rose-50" onClick={() => adjustPercent(t.iron_type, -5)} disabled={savingType === t.iron_type}>
                          <TrendingDown className="h-3.5 w-3.5 mr-1" />-5%
                        </Button>
                        <div className="w-px h-5 bg-border" />
                        <Button size="sm" variant="ghost" className="h-9 px-2 text-emerald-600 hover:bg-emerald-50" onClick={() => adjustPercent(t.iron_type, 5)} disabled={savingType === t.iron_type}>
                          <TrendingUp className="h-3.5 w-3.5 mr-1" />+5%
                        </Button>
                      </div>
                      {/* Rename jenis besi */}
                      <Dialog open={renameOpen === t.iron_type} onOpenChange={(o) => setRenameOpen(o ? t.iron_type : null)}>
                        <DialogTrigger asChild>
                          <Button
                            size="sm" variant="outline"
                            className="h-9 px-2 text-blue-600 hover:bg-blue-50"
                            title="Rename jenis besi"
                            data-testid={`iron-rename-btn-${t.iron_type}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </DialogTrigger>
                        <RenameIronTypeDialog
                          currentName={t.iron_type}
                          productCount={t.product_count}
                          onRename={(newName) => renameIronType(t.iron_type, newName)}
                        />
                      </Dialog>
                      {/* Delete jenis besi (produk otomatis di-unlink, tidak dihapus) */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm" variant="outline"
                            className="h-9 px-2 text-rose-600 hover:bg-rose-50 border-rose-200"
                            title="Hapus jenis besi"
                            data-testid={`iron-delete-type-btn-${t.iron_type}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Hapus jenis besi "{t.iron_type}"?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Daftar harga <b>{t.iron_type}</b> akan dihapus.
                              {t.product_count > 0 ? (
                                <>
                                  <br /><br />
                                  <b>{t.product_count} produk</b> yang terikat dengan jenis ini akan otomatis di-<b>unlink</b> (produknya <b>tetap ada</b> di inventory, tetapi harga tidak lagi auto-hitung dari berat × harga/kg).
                                </>
                              ) : (
                                <> Tidak ada produk yang terikat, aman dihapus.</>
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Batal</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteIronType(t.iron_type)}
                              className="bg-rose-600 hover:bg-rose-700"
                              data-testid={`iron-delete-type-confirm-${t.iron_type}`}
                            >
                              Ya, Hapus Jenis Besi
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="p-3 border-b flex items-center justify-end gap-2">
                    <Dialog open={createOpenType === t.iron_type} onOpenChange={(o) => setCreateOpenType(o ? t.iron_type : null)}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid={`iron-add-product-${t.iron_type}`}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Tambah Produk Besi
                        </Button>
                      </DialogTrigger>
                      <CreateIronProductDialog
                        ironType={t.iron_type}
                        pricePerKg={t.price_per_kg || 0}
                        onSaved={() => { setCreateOpenType(null); load(); }}
                      />
                    </Dialog>
                  </div>
                  {t.products.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">Belum ada produk dengan jenis "{t.iron_type}". Klik <b>Tambah Produk Besi</b> atau <b>Set Config Produk</b> di atas.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nama Produk</TableHead>
                            <TableHead className="text-right">Ukuran</TableHead>
                            <TableHead className="text-right">Panjang</TableHead>
                            <TableHead className="text-right">Berat/Batang</TableHead>
                            <TableHead className="text-right">Harga Jual (auto)</TableHead>
                            <TableHead className="text-right">Stok</TableHead>
                            <TableHead className="text-right">Aksi</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {t.products.map((p) => (
                            <TableRow key={p.id} data-testid={`iron-product-${p.id}`}>
                              <TableCell>
                                <div className="font-medium">{p.name}</div>
                                <div className="text-[11px] text-muted-foreground font-mono">{p.sku}</div>
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {p.iron?.formula === 'round_bar' ? (
                                  <>Ø{p.iron.nominal_size_mm}mm <span className="text-[10px] text-muted-foreground">(real {(p.iron.nominal_size_mm - p.iron.tolerance_mm).toFixed(1)})</span></>
                                ) : (
                                  <Badge variant="outline" className="text-[10px]">manual</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{p.iron?.length_m || DEFAULT_LEN}m</TableCell>
                              <TableCell className="text-right tabular-nums font-medium">{(p.computed_weight_kg || 0).toFixed(2)} kg</TableCell>
                              <TableCell className="text-right tabular-nums font-semibold text-emerald-700">{fmtIDR(p.selling_price)}</TableCell>
                              <TableCell className="text-right tabular-nums">{p.stock}</TableCell>
                              <TableCell className="text-right">
                                <div className="inline-flex gap-1 justify-end">
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        size="sm" variant="ghost"
                                        className="h-7 px-2 text-amber-600 hover:bg-amber-50"
                                        title="Lepas dari kategori besi (produk tetap ada)"
                                        data-testid={`iron-unlink-${p.id}`}
                                      >
                                        <Link2Off className="h-3.5 w-3.5" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Lepas "{p.name}" dari kategori {t.iron_type}?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Produk <b>tetap ada</b> di database, tetapi tidak akan lagi terpengaruh saat Anda mengubah harga per kg {t.iron_type}. Berguna kalau Anda ingin harga produk ini fixed (mis. Besi 6mm) walaupun harga besi beton umum berubah.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Batal</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => unlinkProduct(p.id, p.name)} data-testid={`iron-unlink-confirm-${p.id}`}>
                                          Ya, Lepas Config
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        size="sm" variant="ghost"
                                        className="h-7 px-2 text-rose-600 hover:bg-rose-50"
                                        title="Hapus produk dari database"
                                        data-testid={`iron-delete-${p.id}`}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Hapus "{p.name}" dari database?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Produk akan <b>dihapus permanen</b> dari inventory. Data transaksi lama tetap ada, tapi produk tidak bisa dijual lagi.
                                          <br /><br />
                                          Kalau hanya ingin harga produk ini tidak ikut berubah saat update harga per kg, gunakan tombol <b>Lepas Config</b> (kuning) di sebelah.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Batal</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => deleteProduct(p.id, p.name)} className="bg-rose-600 hover:bg-rose-700" data-testid={`iron-delete-confirm-${p.id}`}>
                                          Ya, Hapus Permanen
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function AddTypeDialog({ onAdd }) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Tambah Jenis Besi</DialogTitle>
        <DialogDescription>Contoh: Besi Beton, Baja IWF, Besi Hollow, Siku, UNP, Plat, dst.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>Nama Jenis Besi</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Besi Beton" data-testid="iron-new-type-name" />
        </div>
        <div>
          <Label>Harga per Kg (Rp)</Label>
          <Input type="number" min="0" step="1" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="12000" data-testid="iron-new-type-price" />
        </div>
      </div>
      <DialogFooter>
        <Button
          disabled={!name.trim() || !Number(price)}
          onClick={() => onAdd(name.trim(), Number(price))}
          data-testid="iron-new-type-save"
        >Simpan</Button>
      </DialogFooter>
    </DialogContent>
  );
}

/** Dialog untuk RENAME jenis besi. Backend akan update semua produk yang terikat. */
function RenameIronTypeDialog({ currentName, productCount, onRename }) {
  const [name, setName] = useState(currentName);
  const [busy, setBusy] = useState(false);
  const disabled = !name.trim() || name.trim() === currentName || busy;
  const submit = async () => {
    setBusy(true);
    try { await onRename(name.trim()); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Rename Jenis Besi</DialogTitle>
        <DialogDescription>
          Semua produk yang menggunakan <b>"{currentName}"</b> akan otomatis diupdate ke nama baru.
          {productCount > 0 && (
            <> Total <b>{productCount} produk</b> akan ter-rename.</>
          )}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>Nama Baru</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="mis. Besi Beton SNI"
            data-testid="iron-rename-input"
            autoFocus
          />
        </div>
      </div>
      <DialogFooter>
        <Button disabled={disabled} onClick={submit} data-testid="iron-rename-save">
          <Save className="h-4 w-4 mr-1.5" /> {busy ? 'Menyimpan...' : 'Simpan'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function CreateIronProductDialog({ ironType, pricePerKg, onSaved }) {
  // ── Mode: 'new' (buat produk baru) atau 'existing' (attach ke produk yg sudah ada)
  const [mode, setMode] = useState('new');

  // ── Common state (formula & rumus berat) — dipakai di kedua mode
  const [formula, setFormula] = useState('round_bar');
  const [nominal, setNominal] = useState('');
  const [tolerance, setTolerance] = useState(String(DEFAULT_TOL));
  const [length, setLength] = useState(String(DEFAULT_LEN));
  const [constant, setConstant] = useState(String(DEFAULT_CONST));
  const [manualWeight, setManualWeight] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Mode "Buat Baru" ────────────────────────────────────────────
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [stock, setStock] = useState('0');
  const [threshold, setThreshold] = useState('5');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');

  // ── Mode "Pilih Existing" ───────────────────────────────────────
  const [existingProducts, setExistingProducts] = useState([]);
  const [existingSearch, setExistingSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [s, ps] = await Promise.all([
          api.get('/suppliers'),
          api.get('/products?active_only=false'),
        ]);
        setSuppliers(s.data);
        setExistingProducts(ps.data || []);
      } catch (e) {
        console.warn('[OwnerIronPrices] gagal load suppliers/products:', e);
      }
    })();
  }, []);

  const previewWeight = useMemo(() => computeWeight({
    formula, nominal_size_mm: nominal, tolerance_mm: tolerance,
    length_m: length, formula_constant: constant, weight_kg: manualWeight,
  }), [formula, nominal, tolerance, length, constant, manualWeight]);

  const previewPrice = useMemo(() => Math.round(previewWeight * Number(pricePerKg || 0)), [previewWeight, pricePerKg]);

  // Auto-generate SKU + name for round_bar (mode 'new' only)
  useEffect(() => {
    if (mode !== 'new') return;
    if (formula === 'round_bar' && nominal) {
      const prefix = ironType.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 12);
      setSku((cur) => cur || `${prefix}-${nominal}mm-${Math.round(Number(length) || 12)}m`);
      setName((cur) => cur || `${ironType} ${nominal}mm ${Math.round(Number(length) || 12)}m`);
    }
  }, [mode, formula, nominal, length, ironType]);

  // Filtered existing product list based on search
  const filteredExisting = useMemo(() => {
    const q = existingSearch.trim().toLowerCase();
    if (!q) return existingProducts;
    return existingProducts.filter((p) =>
      String(p.name || '').toLowerCase().includes(q) ||
      String(p.sku || '').toLowerCase().includes(q)
    );
  }, [existingProducts, existingSearch]);

  const selectedExisting = useMemo(
    () => existingProducts.find((p) => p.id === selectedProductId) || null,
    [existingProducts, selectedProductId]
  );

  const validateFormula = () => {
    if (formula === 'round_bar' && !Number(nominal)) { toast.error('Ukuran nominal wajib'); return false; }
    if (formula === 'manual' && !Number(manualWeight)) { toast.error('Berat manual wajib'); return false; }
    return true;
  };

  const submitNew = async () => {
    if (!name.trim() || !sku.trim()) return toast.error('Nama & SKU wajib');
    if (!validateFormula()) return;
    const initStock = Number(stock) || 0;
    if (initStock > 0 && !supplierId) return toast.error('Pilih supplier jika stok awal > 0');
    setSaving(true);
    try {
      const created = await api.post('/products', {
        sku: sku.trim(),
        name: name.trim(),
        category: ironType,
        selling_price: previewPrice || 1,
        low_stock_threshold: Number(threshold) || 0,
        initial_stock: initStock,
        supplier_id: initStock > 0 ? supplierId : undefined,
      });
      await api.patch(`/products/${created.data.id}/iron`, {
        iron: {
          iron_type: ironType,
          formula,
          nominal_size_mm: Number(nominal) || null,
          tolerance_mm: Number(tolerance) || DEFAULT_TOL,
          length_m: Number(length) || DEFAULT_LEN,
          formula_constant: Number(constant) || DEFAULT_CONST,
          weight_kg: formula === 'manual' ? Number(manualWeight) : null,
        },
      });
      toast.success(`Produk "${name}" ditambahkan ke ${ironType}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSaving(false); }
  };

  const submitExisting = async () => {
    if (!selectedProductId) return toast.error('Pilih produk existing dulu');
    if (!validateFormula()) return;
    setSaving(true);
    try {
      await api.patch(`/products/${selectedProductId}/iron`, {
        iron: {
          iron_type: ironType,
          formula,
          nominal_size_mm: Number(nominal) || null,
          tolerance_mm: Number(tolerance) || DEFAULT_TOL,
          length_m: Number(length) || DEFAULT_LEN,
          formula_constant: Number(constant) || DEFAULT_CONST,
          weight_kg: formula === 'manual' ? Number(manualWeight) : null,
        },
      });
      toast.success(`"${selectedExisting?.name || 'Produk'}" terhubung ke ${ironType} — harga jual otomatis dihitung`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSaving(false); }
  };

  const submit = mode === 'new' ? submitNew : submitExisting;

  const FormulaFields = (
    <>
      <div>
        <Label>Formula Berat</Label>
        <RadioGroup value={formula} onValueChange={setFormula} className="grid grid-cols-2 gap-2">
          <label className={`border rounded p-2 cursor-pointer text-sm ${formula === 'round_bar' ? 'border-primary bg-primary/5' : ''}`}>
            <RadioGroupItem value="round_bar" className="mr-2" data-testid="iron-new-formula-round" />
            Round Bar (Besi Beton)
          </label>
          <label className={`border rounded p-2 cursor-pointer text-sm ${formula === 'manual' ? 'border-primary bg-primary/5' : ''}`}>
            <RadioGroupItem value="manual" className="mr-2" data-testid="iron-new-formula-manual" />
            Manual (IWF/Hollow/dll)
          </label>
        </RadioGroup>
      </div>
      {formula === 'round_bar' ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div>
            <Label className="text-xs">Nominal (mm)</Label>
            <Input type="number" min="0" step="0.1" value={nominal} onChange={(e) => setNominal(e.target.value)} placeholder="8" data-testid="iron-new-nominal" />
          </div>
          <div>
            <Label className="text-xs">Toleransi (mm)</Label>
            <Input type="number" min="0" step="0.01" value={tolerance} onChange={(e) => setTolerance(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Panjang (m)</Label>
            <Input type="number" min="0" step="0.1" value={length} onChange={(e) => setLength(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Konstanta</Label>
            <Input type="number" min="0" step="0.000001" value={constant} onChange={(e) => setConstant(e.target.value)} />
          </div>
        </div>
      ) : (
        <div>
          <Label className="text-xs">Berat per Batang (kg)</Label>
          <Input type="number" min="0" step="0.001" value={manualWeight} onChange={(e) => setManualWeight(e.target.value)} placeholder="15.75" data-testid="iron-new-manual-weight" />
        </div>
      )}
      <div className="rounded-md border bg-accent/40 p-3 flex items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4 text-primary" />
          <div>Berat: <b className="tabular-nums">{previewWeight.toFixed(2)} kg</b></div>
        </div>
        <div>Harga jual (auto): <b className="tabular-nums text-emerald-700">{fmtIDR(previewPrice)}</b></div>
      </div>
    </>
  );

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Tambah Produk ke "{ironType}"</DialogTitle>
        <DialogDescription>Harga jual akan auto-hitung dari <b>berat × Rp {Number(pricePerKg).toLocaleString('id-ID')}/kg</b>.</DialogDescription>
      </DialogHeader>
      <Tabs value={mode} onValueChange={setMode} className="w-full">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="new" data-testid="iron-create-tab-new">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Buat Baru
          </TabsTrigger>
          <TabsTrigger value="existing" data-testid="iron-create-tab-existing">
            <Layers className="h-3.5 w-3.5 mr-1.5" /> Dari Produk Existing
          </TabsTrigger>
        </TabsList>

        {/* MODE: BUAT BARU ────────────────────────────────────────── */}
        <TabsContent value="new">
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {FormulaFields}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">SKU</Label>
                <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="besi-beton-8mm" data-testid="iron-new-sku" />
              </div>
              <div>
                <Label className="text-xs">Nama Produk</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Besi Beton 8mm 12m" data-testid="iron-new-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Stok Awal</Label>
                <Input type="number" min="0" step="1" value={stock} onChange={(e) => setStock(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Low Stock Threshold</Label>
                <Input type="number" min="0" step="1" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
              </div>
            </div>
            {Number(stock) > 0 && (
              <div>
                <Label className="text-xs">Supplier (wajib jika stok awal &gt; 0)</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger><SelectValue placeholder="Pilih supplier..." /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </TabsContent>

        {/* MODE: PILIH EXISTING ───────────────────────────────────── */}
        <TabsContent value="existing">
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="rounded-md border bg-blue-50/60 border-blue-200 p-3 text-xs text-blue-900 flex items-start gap-2">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                Pilih produk dari daftar inventory yang sudah ada. Setelah dihubungkan ke "<b>{ironType}</b>",
                <b> harga jual akan otomatis dihitung ulang</b> dari berat × harga/kg.
                Cocok untuk produk yang sudah dibuat oleh Admin/Kasir sebelum jenis besi ini diatur.
              </div>
            </div>

            <div>
              <Label className="text-xs">Cari Produk</Label>
              <Input
                value={existingSearch}
                onChange={(e) => setExistingSearch(e.target.value)}
                placeholder="Ketik nama atau SKU..."
                data-testid="iron-existing-search"
              />
            </div>

            <div className="border rounded-md max-h-60 overflow-y-auto divide-y">
              {filteredExisting.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Tidak ada produk yang cocok.
                </div>
              ) : (
                filteredExisting.map((p) => {
                  const isSelected = p.id === selectedProductId;
                  const isLinked = !!(p.iron && p.iron.iron_type);
                  const linkedToOther = isLinked && p.iron.iron_type !== ironType;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => setSelectedProductId(p.id)}
                      className={`w-full text-left p-2.5 text-sm flex items-start justify-between gap-2 hover:bg-accent/40 transition-colors ${isSelected ? 'bg-primary/10' : ''}`}
                      data-testid={`iron-existing-option-${p.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{p.name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{p.sku} · Stok: {p.stock}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <div className="tabular-nums text-xs font-semibold">{fmtIDR(p.selling_price)}</div>
                        {linkedToOther && (
                          <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700">
                            terikat: {p.iron.iron_type}
                          </Badge>
                        )}
                        {isLinked && !linkedToOther && (
                          <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">
                            sudah di {ironType}
                          </Badge>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {selectedExisting && (
              <div className="rounded-md border bg-accent/30 p-2.5 text-xs">
                <div className="font-medium">Terpilih: {selectedExisting.name}</div>
                <div className="text-muted-foreground">SKU: {selectedExisting.sku} · Harga saat ini: {fmtIDR(selectedExisting.selling_price)}</div>
                {selectedExisting.iron?.iron_type && selectedExisting.iron.iron_type !== ironType && (
                  <div className="text-amber-700 mt-1">
                    ⚠ Produk ini saat ini terikat ke "<b>{selectedExisting.iron.iron_type}</b>". Simpan akan memindahkan ke "<b>{ironType}</b>".
                  </div>
                )}
              </div>
            )}

            {FormulaFields}
          </div>
        </TabsContent>
      </Tabs>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="iron-new-save">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Menyimpan...' : (mode === 'new' ? 'Buat Produk' : 'Hubungkan ke ' + ironType)}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function AttachIronConfigDialog({ onSaved }) {
  const [products, setProducts] = useState([]);
  const [ironTypes, setIronTypes] = useState([]);
  const [productId, setProductId] = useState('');
  const [ironType, setIronType] = useState('');
  const [formula, setFormula] = useState('round_bar');
  const [nominal, setNominal] = useState('');
  const [tolerance, setTolerance] = useState(String(DEFAULT_TOL));
  const [length, setLength] = useState(String(DEFAULT_LEN));
  const [constant, setConstant] = useState(String(DEFAULT_CONST));
  const [manualWeight, setManualWeight] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [ps, ir] = await Promise.all([
        api.get('/products?active_only=false'),
        api.get('/iron/prices'),
      ]);
      setProducts(ps.data);
      setIronTypes(ir.data.known_iron_types || []);
    })();
  }, []);

  const previewWeight = useMemo(() => computeWeight({
    formula, nominal_size_mm: nominal, tolerance_mm: tolerance,
    length_m: length, formula_constant: constant, weight_kg: manualWeight,
  }), [formula, nominal, tolerance, length, constant, manualWeight]);

  const submit = async () => {
    if (!productId) return toast.error('Pilih produk');
    if (!ironType.trim()) return toast.error('Pilih atau isi jenis besi');
    if (formula === 'round_bar' && !Number(nominal)) return toast.error('Ukuran nominal wajib untuk round bar');
    if (formula === 'manual' && !Number(manualWeight)) return toast.error('Berat manual wajib');
    setSaving(true);
    try {
      const body = {
        iron: {
          iron_type: ironType.trim(),
          formula,
          nominal_size_mm: Number(nominal) || null,
          tolerance_mm: Number(tolerance) || DEFAULT_TOL,
          length_m: Number(length) || DEFAULT_LEN,
          formula_constant: Number(constant) || DEFAULT_CONST,
          weight_kg: formula === 'manual' ? Number(manualWeight) : null,
        },
      };
      await api.patch(`/products/${productId}/iron`, body);
      toast.success('Config besi tersimpan & harga dihitung ulang');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Set Config Besi ke Produk</DialogTitle>
        <DialogDescription>Assign jenis besi & rumus berat ke produk yang sudah ada.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        <div>
          <Label>Produk</Label>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger data-testid="iron-attach-product"><SelectValue placeholder="Pilih produk..." /></SelectTrigger>
            <SelectContent>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Jenis Besi</Label>
          <Input
            list="iron-types"
            value={ironType}
            onChange={(e) => setIronType(e.target.value)}
            placeholder="Besi Beton"
            data-testid="iron-attach-type"
          />
          <datalist id="iron-types">
            {ironTypes.map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
        <div>
          <Label>Formula Berat</Label>
          <RadioGroup value={formula} onValueChange={setFormula} className="grid grid-cols-2 gap-2">
            <label className={`border rounded p-2 cursor-pointer text-sm ${formula === 'round_bar' ? 'border-primary bg-primary/5' : ''}`}>
              <RadioGroupItem value="round_bar" className="mr-2" data-testid="iron-formula-round-bar" />
              Round Bar (Besi Beton)
            </label>
            <label className={`border rounded p-2 cursor-pointer text-sm ${formula === 'manual' ? 'border-primary bg-primary/5' : ''}`}>
              <RadioGroupItem value="manual" className="mr-2" data-testid="iron-formula-manual" />
              Manual (IWF/Hollow/dll)
            </label>
          </RadioGroup>
        </div>
        {formula === 'round_bar' ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Nominal (mm)</Label>
              <Input type="number" min="0" step="0.1" value={nominal} onChange={(e) => setNominal(e.target.value)} placeholder="8" data-testid="iron-nominal" />
            </div>
            <div>
              <Label className="text-xs">Toleransi (mm)</Label>
              <Input type="number" min="0" step="0.01" value={tolerance} onChange={(e) => setTolerance(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Panjang (m)</Label>
              <Input type="number" min="0" step="0.1" value={length} onChange={(e) => setLength(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Konstanta</Label>
              <Input type="number" min="0" step="0.000001" value={constant} onChange={(e) => setConstant(e.target.value)} />
            </div>
          </div>
        ) : (
          <div>
            <Label className="text-xs">Berat per Batang (kg)</Label>
            <Input type="number" min="0" step="0.001" value={manualWeight} onChange={(e) => setManualWeight(e.target.value)} placeholder="15.75" data-testid="iron-manual-weight" />
          </div>
        )}
        <div className="rounded-md border bg-accent/40 p-3 flex items-center gap-2 text-sm">
          <Calculator className="h-4 w-4 text-primary" />
          <div>Preview berat: <b className="tabular-nums">{previewWeight.toFixed(2)} kg</b>/batang</div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="iron-attach-save">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Menyimpan...' : 'Simpan & Terapkan Harga'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
